// src/pages/analytics/CustomerInsightsTab.tsx
//
// Customers → Detail (recipe customer-insights, flag analytics.customers,
// agent 3.22.0+). Every customer's sales this period vs the one before, GP,
// invoices, average ticket, last sale and ordering rhythm, with a status:
// new / growing / steady / declining / lapsed / inactive. Status tiles filter
// the table; a monthly chart shows active / new / lost customers; a segment
// table splits the change by customer category. Clicking a customer opens its
// A/R ledger when analytics.arledger is on.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { CustomerInsightRow, CustomerInsightsResponse, CustomerStatus } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';
import { FilterSelect } from './salesFilterBar';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import CustomerLedgerModal from '../../components/CustomerLedgerModal';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const MONTHS = [3, 6, 12, 24];

const STATUS_META: Record<CustomerStatus, { label: string; cls: string; tile: string; hint: string }> = {
  new:       { label: 'New',       cls: 'bg-sky-500/15 text-sky-300 border-sky-500/40',             tile: 'border-sky-500/30 bg-sky-500/5',         hint: 'First purchase in this period' },
  growing:   { label: 'Growing',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', tile: 'border-emerald-500/30 bg-emerald-500/5', hint: 'Buying more than the prior period' },
  steady:    { label: 'Steady',    cls: 'bg-slate-500/15 text-slate-300 border-slate-500/40',       tile: 'border-slate-600/40 bg-slate-900/40',    hint: 'About the same as the prior period' },
  declining: { label: 'Declining', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',       tile: 'border-amber-500/30 bg-amber-500/5',     hint: 'Buying less than the prior period' },
  lapsed:    { label: 'Lapsed',    cls: 'bg-red-500/15 text-red-300 border-red-500/40',             tile: 'border-red-500/30 bg-red-500/5',         hint: 'Bought in the prior period, nothing recently' },
  inactive:  { label: 'Inactive',  cls: 'bg-slate-700/30 text-slate-500 border-slate-600/40',       tile: 'border-slate-700/40 bg-slate-900/30',    hint: 'No purchases in either period' },
};
const STATUSES = Object.keys(STATUS_META) as CustomerStatus[];
// The agent's maximum: status/search filter in the browser, so fetch everyone.
const CUSTOMER_LIMIT = 5000;

const tooltipProps = {
  cursor: { fill: '#475569', fillOpacity: 0.15 },
  contentStyle: { backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' },
  labelStyle: { color: '#f1f5f9' },
};

type SortKey = 'name' | 'sales' | 'prevSales' | 'changePct' | 'gpPct' | 'invoices' | 'avgTicket' | 'daysSinceLast' | 'avgDaysBetween' | 'balance';

const COLS: CsvColumns<CustomerInsightRow> = [
  ['Code',            (r) => r.code],
  ['Customer',        (r) => r.name],
  ['Status',          (r) => STATUS_META[r.status]?.label ?? r.status],
  ['Salesman',        (r) => r.salesman],
  ['Category',        (r) => r.category],
  ['Area',            (r) => r.area],
  ['City',            (r) => r.city],
  ['State',           (r) => r.state],
  ['Sales',           (r) => r.sales],
  ['Prior Sales',     (r) => r.prevSales],
  ['Change %',        (r) => r.changePct ?? ''],
  ['GP',              (r) => r.gp],
  ['GP %',            (r) => r.gpPct],
  ['Invoices',        (r) => r.invoices],
  ['Avg Ticket',      (r) => r.avgTicket],
  ['First Sale',      (r) => r.firstSale ?? ''],
  ['Last Sale',       (r) => r.lastSale ?? ''],
  ['Days Since Last', (r) => r.daysSinceLast ?? ''],
  ['Avg Days Between',(r) => r.avgDaysBetween ?? ''],
  ['Balance',         (r) => r.balance],
];

function needsNewerAgent(err: unknown): boolean {
  const m = (err as Error)?.message ?? '';
  return /404|unknown recipe|not found/i.test(m);
}

function ChangeCell({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-slate-600">–</span>;
  const cls = pct > 0.05 ? 'text-emerald-400' : pct < -0.05 ? 'text-red-400' : 'text-slate-400';
  return (
    <span className={`tabular-nums inline-flex items-center gap-0.5 ${cls}`}>
      {pct > 0.05 ? <ArrowUp size={10} /> : pct < -0.05 ? <ArrowDown size={10} /> : null}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

export function CustomerInsightsTab() {
  const [months, setMonths] = useState(12);
  const [salesman, setSalesman] = useState('');
  const [customerCategory, setCustomerCategory] = useState('');
  const [area, setArea] = useState('');
  const [status, setStatus] = useState<CustomerStatus | ''>('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('sales');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [ledgerFor, setLedgerFor] = useState<{ code: number; name: string } | null>(null);
  const resetFilters = () => {
    setMonths(12); setSalesman(''); setCustomerCategory(''); setArea(''); setStatus(''); setSearch('');
  };

  const { canSee } = useFeatureAccess();
  const canLedger = canSee('analytics.arledger');
  const pane = useFilterPane();

  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60_000,
  });

  // Status and search filter client-side so the tiles keep their counts. The
  // agent only lists inactive customers when asked for them, so that tile
  // refetches with status=inactive.
  const inactiveOnly = status === 'inactive';
  const q = useQuery({
    queryKey: ['analyticsCustomerInsights', months, salesman, customerCategory, area, inactiveOnly, pane.key],
    queryFn: () => api.getCustomerInsights(
      { months, salesman, customerCategory, area, status: inactiveOnly ? 'inactive' : undefined, limit: CUSTOMER_LIMIT },
      pane.params),
    staleTime: 60_000,
    retry: (n, err) => !needsNewerAgent(err) && n < 1,
  });
  const data: CustomerInsightsResponse | undefined = q.data;

  const rows = useMemo(() => {
    let r = data?.rows ?? [];
    if (status) r = r.filter(x => x.status === status);
    const s = search.trim().toLowerCase();
    if (s) r = r.filter(x => x.name.toLowerCase().includes(s) || String(x.code).includes(s));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name) * dir;
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      return ((av as number) - (bv as number)) * dir;
    });
  }, [data, status, search, sortKey, sortDir]);

  const chartData = useMemo(() => {
    const m = data?.monthly;
    if (!m) return [];
    return m.months.map((month, i) => ({ month, Active: m.active[i] ?? 0, New: m.new[i] ?? 0, Lost: m.lost[i] ?? 0 }));
  }, [data]);

  const sortBy = (k: SortKey) => {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'name' ? 'asc' : 'desc'); }
  };
  const Th = ({ k, label, left }: { k: SortKey; label: string; left?: boolean }) => (
    <th onClick={() => sortBy(k)}
      className={`px-3 py-2.5 font-medium cursor-pointer select-none hover:text-white ${left ? 'text-left' : 'text-right'} ${sortKey === k ? 'text-blue-300' : 'text-slate-400'}`}>
      {label}{sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const opts = (xs?: string[]) => (xs ?? []).map(x => ({ value: x, label: x }));
  const sum = data?.summary;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Users size={20} /> Customers
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Who is buying more, less or not at all
          {data && <span className="text-slate-500"> · {data.dateFrom} → {data.dateTo} vs {data.prevFrom} → {data.prevTo}</span>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Period</span>
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {MONTHS.map(m => <option key={m} value={m}>Last {m} months</option>)}
          </select>
        </div>
        <FilterSelect label="Salesman" value={salesman} onChange={setSalesman} options={opts(filters?.salesmen)} />
        <FilterSelect label="Customer Category" value={customerCategory} onChange={setCustomerCategory} options={opts(filters?.categories)} />
        {(filters?.areas?.length ?? 0) > 0 && (
          <FilterSelect label="Area" value={area} onChange={setArea} options={opts(filters?.areas)} />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Search</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or code…"
              className="pl-7 pr-2 py-1.5 text-xs rounded-md bg-slate-800/50 border border-slate-700/50 text-white w-44 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="customer-insights"
          getState={() => ({ months, salesman, customerCategory, area, status, search })}
          applyState={(s) => {
            if (typeof s.months === 'number' && MONTHS.includes(s.months)) setMonths(s.months);
            if (typeof s.salesman === 'string') setSalesman(s.salesman);
            if (typeof s.customerCategory === 'string') setCustomerCategory(s.customerCategory);
            if (typeof s.area === 'string') setArea(s.area);
            if (typeof s.status === 'string' && (s.status === '' || STATUSES.includes(s.status as CustomerStatus))) setStatus(s.status as CustomerStatus | '');
            if (typeof s.search === 'string') setSearch(s.search);
          }}
        />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button onClick={() => exportCsv('customers', COLS, rows)} disabled={rows.length === 0} title="Export the customer table to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button onClick={() => exportXlsx('customers', COLS, rows)} disabled={rows.length === 0} title="Export the customer table to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <FilterPane />

      {q.error && (
        needsNewerAgent(q.error) ? (
          <div className="p-4 rounded-md bg-slate-800/40 border border-slate-700/40 text-slate-300 text-sm flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-400" /> Needs agent 3.22.0+ — update the agent to see customer insights.
          </div>
        ) : (
          <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            <div>
              <div className="font-medium">Couldn't load customer insights</div>
              <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
            </div>
          </div>
        )
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && sum && (
        <>
          {/* Headline */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Sales</div>
              <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(sum.sales)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">prior {fmtCur(sum.prevSales)}</div>
            </div>
            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Change</div>
              <div className="text-xl font-semibold mt-1"><ChangeCell pct={sum.changePct} /></div>
            </div>
            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Active customers</div>
              <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtNum(sum.active)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">of {fmtNum(sum.customers)}</div>
            </div>
            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Avg ticket</div>
              <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(sum.avgTicket)}</div>
            </div>
          </div>

          {/* Status tiles (click to filter) */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {STATUSES.map(s => {
              const m = STATUS_META[s];
              const on = status === s;
              return (
                <button key={s} onClick={() => setStatus(on ? '' : s)} title={m.hint}
                  className={`rounded-xl border p-3 text-left transition-all ${m.tile} ${on ? 'ring-2 ring-blue-500/60' : 'hover:brightness-125'}`}>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wider">{m.label}</div>
                  <div className="text-lg font-semibold text-white tabular-nums">{fmtNum(sum[s])}</div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Monthly active / new / lost */}
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Customers by month</h3>
              {chartData.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">No monthly data</div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                      <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} interval="preserveStartEnd" minTickGap={24} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} allowDecimals={false} />
                      <Tooltip {...tooltipProps} />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
                      <Bar dataKey="Active" fill="#3b82f6" fillOpacity={0.7} radius={[2, 2, 0, 0]} />
                      <Bar dataKey="New" fill="#10b981" fillOpacity={0.8} radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Lost" fill="#ef4444" fillOpacity={0.8} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Segments */}
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
              <h3 className="text-sm font-semibold text-white px-4 pt-4 pb-2">By customer category</h3>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-slate-400">
                      <th className="px-3 py-2 text-left font-medium">Segment</th>
                      <th className="px-3 py-2 text-right font-medium">Customers</th>
                      <th className="px-3 py-2 text-right font-medium">Sales</th>
                      <th className="px-3 py-2 text-right font-medium">Prior</th>
                      <th className="px-3 py-2 text-right font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bySegment.map(s => (
                      <tr key={s.segment} className="border-b border-slate-800/50">
                        <td className="px-3 py-1.5 text-white">{s.segment || '—'}</td>
                        <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{fmtNum(s.customers)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur(s.sales)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{fmtCur(s.prevSales)}</td>
                        <td className="px-3 py-1.5 text-right"><ChangeCell pct={s.changePct} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.bySegment.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">No segments</div>}
              </div>
            </div>
          </div>

          {/* Customer table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-800">
                  <tr className="border-b border-slate-700/50">
                    <Th k="name" label="Customer" left />
                    <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Status</th>
                    <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Salesman</th>
                    <Th k="sales" label="Sales" />
                    <Th k="prevSales" label="Prior" />
                    <Th k="changePct" label="Change" />
                    <Th k="gpPct" label="GP %" />
                    <Th k="invoices" label="Invoices" />
                    <Th k="avgTicket" label="Avg Ticket" />
                    <Th k="daysSinceLast" label="Last Sale" />
                    <Th k="avgDaysBetween" label="Every" />
                    <Th k="balance" label="Balance" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const m = STATUS_META[r.status] ?? STATUS_META.steady;
                    const overdue = r.daysSinceLast != null && r.avgDaysBetween != null && r.avgDaysBetween > 0
                      && r.daysSinceLast > r.avgDaysBetween * 2;
                    return (
                      <tr key={r.code}
                        onClick={canLedger ? () => setLedgerFor({ code: r.code, name: r.name }) : undefined}
                        title={canLedger ? 'Open the A/R ledger' : undefined}
                        className={`border-b border-slate-800/50 hover:bg-slate-700/20 ${canLedger ? 'cursor-pointer' : ''}`}>
                        <td className="px-3 py-1.5 text-white whitespace-nowrap max-w-[240px] truncate" title={r.name}>
                          {r.name} <span className="text-slate-500">#{r.code}</span>
                          {(r.category || r.area) && <div className="text-[10px] text-slate-500">{[r.category, r.area, r.city].filter(Boolean).join(' · ')}</div>}
                        </td>
                        <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded border text-[10px] ${m.cls}`}>{m.label}</span></td>
                        <td className="px-3 py-1.5 text-slate-400">{r.salesman}</td>
                        <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{fmtCur(r.prevSales)}</td>
                        <td className="px-3 py-1.5 text-right"><ChangeCell pct={r.changePct} /></td>
                        <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{r.sales ? `${r.gpPct.toFixed(1)}%` : '–'}</td>
                        <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{fmtNum(r.invoices)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{r.invoices ? fmtCur(r.avgTicket) : '–'}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${overdue ? 'text-amber-400' : 'text-slate-300'}`}
                          title={overdue ? 'More than twice their usual gap since the last order' : undefined}>
                          {r.lastSale ?? '—'}{r.daysSinceLast != null && <span className="text-slate-500"> · {r.daysSinceLast}d</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-400 tabular-nums">{r.avgDaysBetween != null ? `${Math.round(r.avgDaysBetween)}d` : '–'}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${r.balance > 0 ? 'text-slate-200' : 'text-slate-500'}`}>{fmtCur(r.balance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No customers match</div>}
            </div>
            {data.truncated && (
              <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-800/60">Showing the top customers only — narrow the filters to see the rest.</p>
            )}
          </div>
        </>
      )}

      {ledgerFor && <CustomerLedgerModal code={ledgerFor.code} name={ledgerFor.name} onClose={() => setLedgerFor(null)} />}
    </div>
  );
}
